import { useKeycloak } from "keycloak-react-web";
import AccessDenied from "../components/ui/AccessDenied";
import { hasKeycloakAuthority, hasStaffSignal } from "../app/roles";
import { usePermissions } from "../features/permissions/usePermissions";

/**
 * The portal's only entry gate.
 *
 * It authenticates against the same public Keycloak client the customer app uses, so reaching a
 * login page proves nothing about who is allowed in here — a customer can sign in successfully.
 * Every route therefore has to check for staff, not just for a session. Failing that check renders
 * a refusal rather than returning nothing, so the outcome is visible to the person it happened to
 * and to anyone reading a bug report about it.
 *
 * There are now two questions here and they are kept apart on purpose. **Are you staff at all**, which
 * every route asks; and **do you hold this permission**, which only routes declaring one ask. They
 * send the reader to completely different next actions — sign in as somebody else, versus ask a Super
 * Admin for a permission — so collapsing them into one message would guarantee that half the people
 * who see it do the wrong thing.
 */
const PrivateRoute = ({ children, requiredAuthority, requiredPermission, requiredPermissions = [], requiredAnyPermissions = [] }) => {
  const { keycloak, initialized } = useKeycloak();
  const { can, settled } = usePermissions();

  if (!initialized) return null;
  if (!keycloak.authenticated) return null;
  if (!hasStaffSignal(keycloak)) return <AccessDenied variant="notStaff" />;
  if (requiredAuthority && !hasKeycloakAuthority(keycloak, requiredAuthority)) {
    return <AccessDenied variant="notPermitted" />;
  }

  const permissions = [requiredPermission, ...requiredPermissions].filter(Boolean);
  if (permissions.length > 0 || requiredAnyPermissions.length > 0) {
    // Hold rather than guess while the permission set is in flight. Rendering the page and replacing
    // it with a refusal a moment later reads as a bug; refusing first and then admitting the person
    // reads as a worse one. `settled` is already true when the flag is off, so this costs nothing then.
    if (!settled) return null;
    const missingPermissions = permissions.filter((permission) => !can(permission));
    if (missingPermissions.length > 0) {
      return <AccessDenied variant="missingPermission" permissions={missingPermissions} />;
    }
    if (requiredAnyPermissions.length > 0 && !requiredAnyPermissions.some(can)) {
      return <AccessDenied variant="missingPermission" permissions={requiredAnyPermissions} />;
    }
  }

  return children;
};

export default PrivateRoute;
